import { Alert, Button, Space } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CREST_APP_UPDATE_AVAILABLE_EVENT,
  getBuildVersionIdentity,
  type CrestAppUpdateAvailableDetail,
} from '../lib/build-version-refresh.js';

const UPDATE_PROMPT_DISMISS_PREFIX = 'crest:updatePrompt:dismissed:';

export function AppUpdatePrompt() {
  const { i18n } = useTranslation();
  const [update, setUpdate] = useState<CrestAppUpdateAvailableDetail | null>(null);
  const dismissedBuildIdsRef = useRef(new Set<string>());

  useEffect(() => {
    const handleUpdateAvailable = (event: Event) => {
      const detail = (event as CustomEvent<CrestAppUpdateAvailableDetail>).detail;
      const dismissKey = detail ? buildDismissKey(detail) : null;
      if (
        !detail?.next?.buildId ||
        !dismissKey ||
        dismissedBuildIdsRef.current.has(dismissKey) ||
        isDismissed(dismissKey)
      ) {
        return;
      }
      setUpdate(detail);
    };

    window.addEventListener(CREST_APP_UPDATE_AVAILABLE_EVENT, handleUpdateAvailable);
    return () => {
      window.removeEventListener(CREST_APP_UPDATE_AVAILABLE_EVENT, handleUpdateAvailable);
    };
  }, []);

  if (!update) return null;

  const isEn = i18n.language?.startsWith('en');
  const appName = isEn ? update.appNameEn : update.appNameZh;
  const builtAt = update.next.builtAt ? new Date(update.next.builtAt) : null;
  const builtAtText =
    builtAt && !Number.isNaN(builtAt.getTime())
      ? builtAt.toLocaleString(isEn ? 'en-US' : 'zh-CN')
      : null;
  const copy = isEn
    ? {
        title: `${appName} update available`,
        description: builtAtText
          ? `A new ${appName} version was published at ${builtAtText}. Refresh to use it. Your current page will not be reloaded automatically.`
          : `A new ${appName} version is available. Refresh to use it. Your current page will not be reloaded automatically.`,
        later: 'Later',
        refresh: 'Refresh now',
      }
    : {
        title: `${appName}已更新`,
        description: builtAtText
          ? `${appName}在 ${builtAtText} 发布了新版本。刷新后即可使用最新版本，当前页面不会被自动打断。`
          : `${appName}发布了新版本。刷新后即可使用最新版本，当前页面不会被自动打断。`,
        later: '稍后',
        refresh: '立即刷新',
      };

  const dismiss = () => {
    const dismissKey = buildDismissKey(update);
    dismissedBuildIdsRef.current.add(dismissKey);
    rememberDismissed(dismissKey);
    setUpdate(null);
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 72,
        right: 24,
        zIndex: 2000,
        width: 'min(420px, calc(100vw - 32px))',
        boxShadow: '0 10px 30px rgba(15, 23, 42, 0.12)',
      }}
    >
      <Alert
        showIcon
        type="info"
        message={copy.title}
        description={copy.description}
        action={
          <Space direction="vertical" size={8}>
            <Button size="small" type="primary" onClick={() => window.location.reload()}>
              {copy.refresh}
            </Button>
            <Button size="small" onClick={dismiss}>
              {copy.later}
            </Button>
          </Space>
        }
        closable
        onClose={dismiss}
      />
    </div>
  );
}

function buildDismissKey(update: CrestAppUpdateAvailableDetail) {
  return `${update.appKind}:${getBuildVersionIdentity(update.next)}`;
}

function isDismissed(dismissKey: string) {
  try {
    return window.localStorage.getItem(`${UPDATE_PROMPT_DISMISS_PREFIX}${dismissKey}`) === '1';
  } catch {
    return false;
  }
}

function rememberDismissed(dismissKey: string) {
  try {
    window.localStorage.setItem(`${UPDATE_PROMPT_DISMISS_PREFIX}${dismissKey}`, '1');
  } catch {
    // localStorage 在隐私模式或内嵌浏览器中可能不可用；内存去重仍然有效。
  }
}
